/**
 * Revenue Reporting Dashboard
 * Financial analytics by company, product, channel
 * Real-time revenue tracking and forecasting
 */

import React, { useState, useEffect } from 'react';
import { BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { supabase } from '@/lib/supabase';
import { AdminPageLayout, StatsGrid } from '@/components/layout/AdminPageLayout';
import { StatCard } from '@/components/layout/AdminStatCard';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { TrendingUp, DollarSign, Target, Users } from 'lucide-react';

export default function RevenueReporting() {
  const [dateRange, setDateRange] = useState<'month' | 'quarter' | 'year'>('month');
  const [revenueByCompany, setRevenueByCompany] = useState<any[]>([]);
  const [revenueByProduct, setRevenueByProduct] = useState<any[]>([]);
  const [revenueByChannel, setRevenueByChannel] = useState<any[]>([]);
  const [monthlyTrend, setMonthlyTrend] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadRevenueData();
  }, [dateRange]);

  const loadRevenueData = async () => {
    setLoading(true);
    await Promise.all([
      loadByCompany(),
      loadByProduct(),
      loadByChannel(),
      loadMonthlyTrend()
    ]);
    setLoading(false);
  };

  const loadByCompany = async () => {
    const { data } = await supabase
      .from('warranty_agreements')
      .select('company, annual_price_cents')
      .eq('status', 'active');

    if (data) {
      const grouped = data.reduce((acc: any, row: any) => {
        const company = row.company || 'Unknown';
        acc[company] = (acc[company] || 0) + (row.annual_price_cents || 0);
        return acc;
      }, {});

      const chartData = Object.entries(grouped).map(([name, value]) => ({
        name,
        revenue: (value as number) / 100
      }));

      setRevenueByCompany(chartData);
    }
  };

  const loadByProduct = async () => {
    const { data } = await supabase
      .from('warranty_agreements')
      .select('plan_name, annual_price_cents')
      .eq('status', 'active');

    if (data) {
      const grouped = data.reduce((acc: any, row: any) => {
        const plan = row.plan_name || 'Unknown';
        acc[plan] = (acc[plan] || 0) + (row.annual_price_cents || 0);
        return acc;
      }, {});

      const chartData = Object.entries(grouped).map(([name, value]) => ({
        name,
        value: (value as number) / 100
      }));

      setRevenueByProduct(chartData);
    }
  };

  const loadByChannel = async () => {
    // Simulated data - in production would track lead source
    setRevenueByChannel([
      { name: 'Direct', value: 45000 },
      { name: 'Partner', value: 32000 },
      { name: 'Online', value: 28000 },
      { name: 'Referral', value: 15000 }
    ]);
  };

  const loadMonthlyTrend = async () => {
    // Simulated monthly trend
    setMonthlyTrend([
      { month: 'Jan', revenue: 95000, target: 100000 },
      { month: 'Feb', revenue: 105000, target: 100000 },
      { month: 'Mar', revenue: 115000, target: 110000 },
      { month: 'Apr', revenue: 120000, target: 115000 },
      { month: 'May', revenue: 125000, target: 120000 },
      { month: 'Jun', revenue: 130000, target: 125000 }
    ]);
  };

  const COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6'];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="text-4xl mb-4">📊</div>
          <div className="text-gray-600">Loading revenue data...</div>
        </div>
      </div>
    );
  }

  const totalRevenue = revenueByCompany.reduce((sum, item) => sum + item.revenue, 0);
  const projectedAnnual = totalRevenue * 12; // Assuming monthly data

  return (
    <AdminPageLayout
      title="Revenue Reporting"
      subtitle="Financial analytics and forecasting"
      actions={
        <>
          {(['month', 'quarter', 'year'] as const).map((range) => (
            <button
              key={range}
              onClick={() => setDateRange(range)}
              className={`px-4 py-2 rounded-lg font-medium ${
                dateRange === range
                  ? 'bg-blue-600 text-white'
                  : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
              }`}
            >
              {range === 'month' ? 'This Month' : range === 'quarter' ? 'Quarter' : 'Year'}
            </button>
          ))}
        </>
      }
    >
      {/* Summary Cards */}
      <StatsGrid cols={4}>
        <StatCard
          label="Total Revenue"
          value={`$${totalRevenue.toLocaleString()}`}
          icon={DollarSign}
          subtitle="↑ 12% from last period"
        />
        <StatCard
          label="Projected Annual"
          value={`$${projectedAnnual.toLocaleString()}`}
          icon={TrendingUp}
          subtitle="Based on current run rate"
        />
        <StatCard
          label="Avg Deal Size"
          value={`$${(totalRevenue / Math.max(revenueByProduct.length, 1)).toFixed(0)}`}
          icon={Target}
          subtitle="Per agreement"
        />
        <StatCard
          label="Active Agreements"
          value={revenueByProduct.length}
          icon={Users}
          subtitle="↑ 8% growth"
        />
      </StatsGrid>

      {/* Monthly Trend */}
      <Card className="p-6">
        <h2 className="text-lg font-semibold mb-4">Revenue Trend vs Target</h2>
        <ResponsiveContainer width="100%" height={300}>
          <LineChart data={monthlyTrend}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="month" />
            <YAxis />
            <Tooltip formatter={(value: any) => `$${value.toLocaleString()}`} />
            <Legend />
            <Line type="monotone" dataKey="revenue" stroke="#10B981" strokeWidth={2} name="Actual Revenue" />
            <Line type="monotone" dataKey="target" stroke="#6B7280" strokeWidth={2} strokeDasharray="5 5" name="Target" />
          </LineChart>
        </ResponsiveContainer>
      </Card>

      <div className="grid grid-cols-2 gap-6">
        {/* Revenue by Company */}
        <Card className="p-6">
          <h2 className="text-lg font-semibold mb-4">Revenue by Company</h2>
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={revenueByCompany}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis />
              <Tooltip formatter={(value: any) => `$${value.toLocaleString()}`} />
              <Bar dataKey="revenue" fill="#3B82F6" />
            </BarChart>
          </ResponsiveContainer>
        </Card>

        {/* Revenue by Product */}
        <Card className="p-6">
          <h2 className="text-lg font-semibold mb-4">Revenue by Product</h2>
          <ResponsiveContainer width="100%" height={250}>
            <PieChart>
              <Pie
                data={revenueByProduct.slice(0, 5)}
                cx="50%"
                cy="50%"
                label={(entry) => `${entry.name}: $${entry.value.toLocaleString()}`}
                outerRadius={80}
                dataKey="value"
              >
                {revenueByProduct.slice(0, 5).map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip formatter={(value: any) => `$${value.toLocaleString()}`} />
            </PieChart>
          </ResponsiveContainer>
        </Card>
      </div>

      {/* Revenue by Channel */}
      <Card className="p-6">
        <h2 className="text-lg font-semibold mb-4">Revenue by Channel</h2>
        <div className="grid grid-cols-4 gap-4">
          {revenueByChannel.map((channel, index) => (
            <div key={channel.name} className="text-center p-4 border rounded-lg">
              <div className="text-2xl font-bold" style={{ color: COLORS[index % COLORS.length] }}>
                ${channel.value.toLocaleString()}
              </div>
              <div className="text-sm text-gray-600 mt-1">{channel.name}</div>
              <div className="text-xs text-gray-500 mt-1">
                {((channel.value / totalRevenue) * 100).toFixed(1)}%
              </div>
            </div>
          ))}
        </div>
      </Card>
    </AdminPageLayout>
  );
}
